import { Label, Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/24/outline";
import { BaseSelectModel } from "components/typings";
import { Fragment, ReactNode } from "react";
import { cn } from "utils/ui";

export interface SelectProps<T extends BaseSelectModel> {
  label?: string;
  error?: string;
  selectedItem: T | null;
  onSelection: (selection: T) => void;
  items: T[];
  renderName: (value: T | null) => ReactNode;
}

function Select<T extends BaseSelectModel>({
  items = [],
  label,
  error,
  selectedItem,
  onSelection,
  renderName,
}: SelectProps<T>) {
  const hasError = typeof error === "string" && error.length > 0;
  return (
    <div>
      <Listbox value={selectedItem ?? undefined} onChange={onSelection}>
        {({ open }) => (
          <>
            {label && <Label className="text-color-secondary text-sm font-medium">{label}</Label>}
            <div className="relative mt-1">
              <ListboxButton
                tabIndex={0}
                autoFocus={true}
                className={cn(
                  "relative w-full cursor-pointer rounded-md py-2 pl-3 pr-10 text-left shadow-sm focus:outline-none sm:text-sm",
                  "border focus:ring-1 dark:border-slate-700 dark:bg-slate-600 dark:text-slate-200 dark:focus:ring-2",
                  hasError
                    ? "border-red-300 focus:border-red-500 focus:ring-red-400 dark:border-red-400"
                    : "border-gray-300 focus:border-ribbon-400 focus:ring-ribbon-400"
                )}
              >
                <span className="block truncate">{renderName(selectedItem)}</span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon className="size-5 text-gray-400" aria-hidden="true" />
                </span>
              </ListboxButton>

              <Transition
                show={open}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <ListboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg focus:outline-none dark:bg-slate-700 sm:text-sm">
                  {items.map((item) => (
                    <ListboxOption
                      key={item.id}
                      className={
                        "group relative cursor-default select-none py-2 pl-8 pr-4 text-gray-900 data-[focus]:bg-ribbon-500 data-[focus]:text-white dark:text-slate-300 dark:data-[focus]:bg-ribbon-400 dark:data-[focus]:text-slate-200"
                      }
                      value={item}
                    >
                      <span
                        className={cn(
                          selectedItem?.id === item.id ? "font-semibold dark:text-slate-200" : "font-normal",
                          "block truncate"
                        )}
                      >
                        {renderName(item)}
                      </span>

                      {selectedItem?.id === item.id ? (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-1.5 text-ribbon-600 group-data-[focus]:text-white dark:text-ribbon-400 dark:group-data-[focus]:text-slate-200">
                          <CheckIcon className="size-5" aria-hidden="true" />
                        </span>
                      ) : null}
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </Transition>
            </div>
          </>
        )}
      </Listbox>
      {hasError ? (
        <span className="mt-1 text-sm text-red-600 dark:text-red-500" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

export default Select;
